import {
  UserRole,
  PermissionResource,
  PermissionAction,
  DEFAULT_PERMISSIONS,
} from '../types'
import type { PermissionMatrix, UserPermissions, User } from '../types'

/**
 * Creates a UserPermissions object from a user's role
 */
export function createUserPermissions(user: User): UserPermissions | null {
  if (!user.role) {
    return null
  }

  const permissions = DEFAULT_PERMISSIONS[user.role]
  if (!permissions) {
    return null
  }

  return {
    role: user.role,
    permissions,
    canManageUsers: () =>
      permissions.users.create &&
      permissions.users.update &&
      permissions.users.delete,
    canManageRoles: () =>
      permissions.roles.create &&
      permissions.roles.update &&
      permissions.roles.delete,
    canCreateSolicitudes: () => permissions.solicitudes.create,
    canEvaluatePostulaciones: () => permissions.postulaciones.evaluate,
    canViewReports: () => permissions.reports.view,
    canExportReports: () => permissions.reports.export,
    canQualifyCandidates: () => permissions.postulaciones.qualify,
    canBulkQualifyCandidates: () => permissions.postulaciones.bulk_qualify,
    hasPermission: (resource: PermissionResource, action: PermissionAction) => {
      return hasPermission(permissions, resource, action)
    },
  }
}

/**
 * Checks if a permission matrix allows a specific resource and action
 */
export function hasPermission(
  permissions: PermissionMatrix,
  resource: PermissionResource,
  action: PermissionAction
): boolean {
  const resourcePermissions = permissions[resource]
  if (!resourcePermissions) {
    return false
  }

  return (
    resourcePermissions[action as keyof typeof resourcePermissions] === true
  )
}

/**
 * Checks if a user has a specific role
 */
export function hasRole(user: User, role: UserRole): boolean {
  return user.role === role
}

/**
 * Checks if a user is an administrator
 */
export function isAdmin(user: User): boolean {
  return hasRole(user, UserRole.ADMINISTRADOR)
}

/**
 * Checks if a user can access user management features
 */
export function canAccessUserManagement(user: User): boolean {
  return isAdmin(user)
}

/**
 * Checks if a user can access role management features
 */
export function canAccessRoleManagement(user: User): boolean {
  return isAdmin(user)
}

/**
 * Gets the display name for a user role
 */
export function getRoleDisplayName(role: UserRole): string {
  const roleNames: Record<UserRole, string> = {
    [UserRole.ADMINISTRADOR]: 'Administrador',
    [UserRole.EDITOR]: 'Editor',
    [UserRole.VISUALIZADOR]: 'Visualizador',
  }

  return roleNames[role] || role
}

/**
 * Gets all available roles as options for forms
 */
export function getRoleOptions(): Array<{ value: UserRole; label: string }> {
  return Object.values(UserRole).map(role => ({
    value: role,
    label: getRoleDisplayName(role),
  }))
}

/**
 * Validates if a role name is valid
 */
export function isValidRole(role: string): role is UserRole {
  return Object.values(UserRole).includes(role as UserRole)
}

/**
 * Gets the permission matrix for a specific role
 */
export function getPermissionsForRole(role: UserRole): PermissionMatrix {
  return DEFAULT_PERMISSIONS[role]
}

/**
 * Checks if a role is a system role that cannot be deleted
 */
export function isSystemRole(roleName: string): boolean {
  return Object.values(UserRole).includes(roleName as UserRole)
}

/**
 * Gets user-friendly error messages for user management errors
 */
export function getUserManagementErrorMessage(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    USER_NOT_FOUND: 'Usuario no encontrado',
    EMAIL_ALREADY_EXISTS: 'El email ya está registrado',
    INVALID_ROLE: 'Rol inválido seleccionado',
    CANNOT_DELETE_SELF: 'No puedes eliminar tu propia cuenta',
    CANNOT_DELETE_LAST_ADMIN: 'No se puede eliminar el último administrador',
    ROLE_NOT_FOUND: 'Rol no encontrado',
    CANNOT_DELETE_SYSTEM_ROLE: 'No se puede eliminar un rol del sistema',
    ROLE_HAS_USERS: 'No se puede eliminar un rol que tiene usuarios asignados',
    INSUFFICIENT_PERMISSIONS: 'Permisos insuficientes para esta acción',
  }

  return errorMessages[errorCode] || 'Error desconocido'
}

/**
 * Extracts user role from Cognito groups
 */
export function extractRoleFromCognitoGroups(
  groups: string[]
): UserRole | null {
  for (const group of groups) {
    if (isValidRole(group)) {
      return group as UserRole
    }
  }
  return null
}

/**
 * Checks if a user can perform an action on another user
 */
export function canManageUser(currentUser: User, targetUser: User): boolean {
  // Users cannot manage themselves for certain operations
  if (currentUser.id === targetUser.id) {
    return false
  }

  // Only admins can manage other users
  return isAdmin(currentUser)
}

/**
 * Checks if deleting a user would remove the last administrator
 */
export function wouldRemoveLastAdmin(
  users: User[],
  userToDelete: User
): boolean {
  const adminUsers = users.filter(
    user => user.role === UserRole.ADMINISTRADOR && user.id !== userToDelete.id
  )

  return adminUsers.length === 0 && userToDelete.role === UserRole.ADMINISTRADOR
}
